// Shared UI Components for Modern Design System - Goodwin Hospice
// Gradient cards, headers, and modern styling components

import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { cn } from '@/lib/utils';

// Gradient variants for cards - Goodwin Brand Palette
export const GRADIENTS = {
  // Primary brand teal variations
  teal: 'from-[#00b0ab] to-[#008a86]',
  primary: 'from-[#00b0ab] to-[#008a86]',
  brand: 'from-[#00b0ab] via-[#009590] to-[#48494b]',
  // Supporting colors
  emerald: 'from-emerald-500 to-teal-600',
  blue: 'from-cyan-500 to-[#00b0ab]',
  amber: 'from-amber-400 to-orange-500',
  rose: 'from-rose-500 to-red-600',
  purple: 'from-purple-500 to-violet-600',
  green: 'from-emerald-500 to-green-600',
  indigo: 'from-[#00b0ab] to-cyan-600',
  pink: 'from-pink-500 to-rose-500'
};

export const SHADOW_COLORS = {
  teal: 'shadow-[#00b0ab]/20 hover:shadow-[#00b0ab]/30',
  primary: 'shadow-[#00b0ab]/20 hover:shadow-[#00b0ab]/30',
  brand: 'shadow-[#00b0ab]/20 hover:shadow-[#00b0ab]/30',
  indigo: 'shadow-[#00b0ab]/20 hover:shadow-[#00b0ab]/30',
  blue: 'shadow-cyan-500/20 hover:shadow-cyan-500/30',
  emerald: 'shadow-emerald-500/20 hover:shadow-emerald-500/30',
  amber: 'shadow-amber-500/20 hover:shadow-amber-500/30',
  rose: 'shadow-rose-500/20 hover:shadow-rose-500/30',
  purple: 'shadow-purple-500/20 hover:shadow-purple-500/30',
  green: 'shadow-green-500/20 hover:shadow-green-500/30',
  pink: 'shadow-pink-500/20 hover:shadow-pink-500/30'
};

// Page Header with Gradient Background - Goodwin Brand
export function PageHeader({ icon: Icon, title, subtitle, children, actions, gradient = 'from-[#00b0ab] via-[#009590] to-[#48494b]' }) {
  return (
    <div className={`relative overflow-hidden rounded-2xl bg-gradient-to-r ${gradient} p-6 text-white shadow-xl`}>
      <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4xIj48cGF0aCBkPSJNMzYgMzRjMC0yIDItNCAyLTRzMiAyIDIgNC0yIDQtMiA0LTItMi0yLTR6Ii8+PC9nPjwvZz48L3N2Zz4=')] opacity-30"></div>
      <div className="relative flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3">
            {Icon && (
              <div className="p-2 bg-white/20 rounded-xl backdrop-blur-sm">
                <Icon className="h-7 w-7" />
              </div>
            )}
            {title}
          </h1>
          {subtitle && (
            <p className="text-white/80 mt-2 text-lg">{subtitle}</p>
          )}
        </div>
        {(children || actions) && (
          <div className="flex gap-2">
            {actions || children}
          </div>
        )}
      </div>
    </div>
  );
}

// Gradient Metric Card
export function MetricCard({ 
  title, 
  value, 
  subtitle,
  icon: Icon, 
  gradient = 'indigo',
  className,
  trend,
  trendValue
}) {
  return (
    <Card className={cn(
      `group relative overflow-hidden bg-gradient-to-br ${GRADIENTS[gradient]} text-white border-0 shadow-lg ${SHADOW_COLORS[gradient]} hover:shadow-xl transition-all duration-300 hover:-translate-y-1`,
      className
    )}>
      <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent"></div>
      <CardContent className="pt-6 relative">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm opacity-90 font-medium">{title}</p>
            <p className="text-4xl font-bold mt-1">{value}</p>
            {subtitle && <p className="text-sm opacity-75 mt-1">{subtitle}</p>}
            {trend && (
              <p className={`text-sm mt-1 ${trend === 'up' ? 'text-green-200' : trend === 'down' ? 'text-red-200' : 'text-white/70'}`}>
                {trend === 'up' ? '↑' : trend === 'down' ? '↓' : '→'} {trendValue}
              </p>
            )}
          </div>
          {Icon && (
            <div className="p-3 bg-white/20 rounded-xl">
              <Icon className="h-8 w-8" />
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// Status Badge with Gradient
export function StatusBadge({ status, size = 'default' }) {
  const statusConfig = {
    'CRITICAL': { gradient: 'from-rose-500 to-red-600', text: 'CRITICAL' },
    'HIGH': { gradient: 'from-orange-500 to-amber-600', text: 'HIGH' },
    'MEDIUM': { gradient: 'from-amber-400 to-yellow-500', text: 'MEDIUM', textColor: 'text-black' },
    'MODERATE': { gradient: 'from-amber-400 to-yellow-500', text: 'MODERATE', textColor: 'text-black' },
    'LOW': { gradient: 'from-emerald-500 to-green-600', text: 'LOW' },
    'HEALTHY': { gradient: 'from-emerald-500 to-green-600', text: 'HEALTHY' },
    'AT RISK': { gradient: 'from-amber-500 to-orange-600', text: 'AT RISK' },
    'MET': { gradient: 'from-emerald-500 to-green-600', text: 'MET' },
    'EXCEEDED': { gradient: 'from-blue-500 to-indigo-600', text: 'EXCEEDED' },
    'BELOW': { gradient: 'from-rose-500 to-red-600', text: 'BELOW' },
    'PENDING': { gradient: 'from-gray-400 to-gray-500', text: 'PENDING' }
  };
  
  const config = statusConfig[status] || statusConfig['PENDING'];
  
  return (
    <Badge className={cn(
      `bg-gradient-to-r ${config.gradient} ${config.textColor || 'text-white'} font-medium shadow-sm border-0`,
      size === 'lg' && 'px-3 py-1 text-sm',
      size === 'sm' && 'px-2 py-0.5 text-xs'
    )}>
      {config.text}
    </Badge>
  );
}

// Team Badge with Gradient
export function TeamBadge({ team }) {
  const teamConfig = {
    'Blue': { gradient: 'from-blue-500 to-cyan-500' },
    'Yellow': { gradient: 'from-amber-400 to-yellow-500', textColor: 'text-black' },
    'Green': { gradient: 'from-emerald-500 to-teal-500' },
    'Wound RN': { gradient: 'from-purple-500 to-violet-500' }
  };
  
  const config = teamConfig[team] || { gradient: 'from-gray-400 to-gray-500' };
  
  return (
    <Badge className={cn(
      `bg-gradient-to-r ${config.gradient} ${config.textColor || 'text-white'} font-medium shadow-sm border-0`
    )}>
      {team || 'N/A'}
    </Badge>
  );
}

// Discipline Badge
export function DisciplineBadge({ discipline }) {
  const disciplineConfig = {
    'RN': { gradient: 'from-blue-500 to-indigo-500' },
    'CM': { gradient: 'from-indigo-500 to-violet-500' },
    'LPN': { gradient: 'from-cyan-500 to-teal-500' },
    'Wound RN': { gradient: 'from-purple-500 to-pink-500' },
    'PRN': { gradient: 'from-gray-500 to-slate-600' },
    'Chaplain': { gradient: 'from-amber-500 to-orange-500' },
    'SW': { gradient: 'from-emerald-500 to-green-500' },
    'Aide': { gradient: 'from-teal-500 to-cyan-500' }
  };
  
  const config = disciplineConfig[discipline] || { gradient: 'from-gray-400 to-gray-500' };
  
  return (
    <Badge className={cn(
      `bg-gradient-to-r ${config.gradient} text-white font-medium shadow-sm border-0`
    )}>
      {discipline}
    </Badge>
  );
}

// Progress Bar with Gradient
export function GradientProgress({ value, max = 100, showLabel = true, variant = 'default' }) {
  const percentage = Math.min((value / max) * 100, 100);
  
  let barColor = 'bg-gradient-to-r from-emerald-500 to-green-500';
  if (percentage >= 90) {
    barColor = 'bg-gradient-to-r from-rose-500 to-red-500';
  } else if (percentage >= 75) {
    barColor = 'bg-gradient-to-r from-amber-500 to-orange-500';
  }
  
  return (
    <div className="w-full">
      <div className="flex justify-between items-center mb-1">
        {showLabel && (
          <>
            <span className="text-sm text-muted-foreground">{value}</span>
            <span className="text-sm text-muted-foreground">{percentage.toFixed(0)}%</span>
          </>
        )}
      </div>
      <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
        <div 
          className={`h-full ${barColor} rounded-full transition-all duration-500`}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
}

// Risk Gauge
export function RiskGauge({ score, size = 'md' }) {
  const getColor = (score) => {
    if (score >= 85) return '#EF4444';
    if (score >= 70) return '#F59E0B';
    return '#10B981';
  };
  
  const dimensions = {
    sm: { svg: 80, radius: 30, stroke: 6, text: 'text-lg' },
    md: { svg: 112, radius: 48, stroke: 10, text: 'text-2xl' },
    lg: { svg: 140, radius: 60, stroke: 12, text: 'text-3xl' }
  };
  
  const dim = dimensions[size];
  const circumference = 2 * Math.PI * dim.radius;
  const strokeDasharray = `${(score / 100) * circumference} ${circumference}`;
  const center = dim.svg / 2;
  
  return (
    <div className="relative inline-flex items-center justify-center">
      <svg 
        className="transform -rotate-90 drop-shadow-lg" 
        width={dim.svg} 
        height={dim.svg}
      >
        <circle 
          cx={center} 
          cy={center} 
          r={dim.radius} 
          stroke="#e5e7eb" 
          strokeWidth={dim.stroke} 
          fill="none" 
        />
        <circle
          cx={center}
          cy={center}
          r={dim.radius}
          stroke={getColor(score)}
          strokeWidth={dim.stroke}
          fill="none"
          strokeDasharray={strokeDasharray}
          strokeLinecap="round"
          className="transition-all duration-700 ease-out"
        />
      </svg>
      <span className={`absolute ${dim.text} font-bold`}>
        {score.toFixed(0)}
      </span>
    </div>
  );
}

// Modern Tabs Style
export const modernTabsClass = "flex flex-wrap h-auto gap-1 p-1.5 bg-slate-100 dark:bg-slate-800 rounded-xl";
export const modernTabTriggerClass = "data-[state=active]:bg-white data-[state=active]:shadow-sm rounded-lg px-4 py-2.5 transition-all";

// Glass Button
export function GlassButton({ children, onClick, disabled, className, ...props }) {
  return (
    <Button
      variant="secondary"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        "bg-white/20 hover:bg-white/30 text-white border-0 backdrop-blur-sm",
        className
      )}
      {...props}
    >
      {children}
    </Button>
  );
}

// Modern Card with subtle gradient
export function ModernCard({ children, className, highlight = false, ...props }) {
  return (
    <Card 
      className={cn(
        "transition-all duration-300 hover:shadow-lg",
        highlight && "ring-2 ring-[#00b0ab]/20",
        className
      )}
      {...props}
    >
      {children}
    </Card>
  );
}

// Section Header
export function SectionHeader({ icon: Icon, title, subtitle, action }) {
  return (
    <div className="flex items-center justify-between mb-4">
      <div>
        <h3 className="text-lg font-semibold flex items-center gap-2">
          {Icon && <Icon className="h-5 w-5 text-[#00b0ab]" />}
          {title}
        </h3>
        {subtitle && <p className="text-sm text-muted-foreground">{subtitle}</p>}
      </div>
      {action}
    </div>
  );
}

export default {
  PageHeader,
  MetricCard,
  StatusBadge,
  TeamBadge,
  DisciplineBadge,
  GradientProgress,
  RiskGauge,
  GlassButton,
  ModernCard,
  SectionHeader,
  GRADIENTS,
  SHADOW_COLORS,
  modernTabsClass,
  modernTabTriggerClass
};
